// POS System seed data — products, ingredients, suppliers, recipes
// Run seedPosData() once on first setup to populate localStorage + Firestore

import { savePosProduct, saveInventoryItem, saveSupplier, saveRecipe, saveOverhead } from '../lib/posStore'

// ────────────────────────────────────────────
// POS Product Catalog
// ────────────────────────────────────────────

// POS sub-categories (tabs within the POS terminal)
export const POS_DEPARTMENTS = [
  { id: 'kitchen', label: 'Kitchen' },
  { id: 'bakery', label: 'Bakery' },
  { id: 'sandwich', label: 'Sandwiches' },
  { id: 'drinks', label: 'Soft Drinks' },
  { id: 'tea', label: 'Tea & Hot Drinks' },
  { id: 'sweets', label: 'Sweets & Chips' },
  { id: 'misc', label: 'Miscellaneous' },
]

// Map POS sub-categories to business departments
// kitchen, bakery, yawehs are the 3 business departments
export const POS_DEPT_MAP = {
  kitchen: ['kitchen', 'sandwich', 'drinks', 'tea', 'sweets', 'misc'],
  bakery: ['bakery'],
  yawehs: ['bakery'],
}

// Get POS sub-category tabs for a business department
export function getPosDepartments(departmentId) {
  const ids = POS_DEPT_MAP[departmentId] || []
  return POS_DEPARTMENTS.filter(d => ids.includes(d.id))
}

// Map a POS sub-category to its business departmentId
export function posDeptToBizDept(posDept) {
  if (['kitchen', 'sandwich', 'drinks', 'tea', 'sweets', 'misc'].includes(posDept)) return 'kitchen'
  if (posDept === 'bakery') return 'bakery' // default to desres bakery; yawehs handled at page level
  return 'kitchen'
}

export const POS_PRODUCTS_SEED = [
  // ── Kitchen ──
  { code: 'FEG', name: 'Full Egg (Fried)', department: 'kitchen', category: 'eggs', sellingPrice: 10, type: 'manufactured' },
  { code: 'FS', name: 'Full Steak', department: 'kitchen', category: 'steak', sellingPrice: 50, type: 'manufactured' },
  { code: 'HMK', name: 'Half Makhaha', department: 'kitchen', category: 'makhaha', sellingPrice: 15, type: 'manufactured' },
  { code: 'FMK', name: 'Full Makhaha', department: 'kitchen', category: 'makhaha', sellingPrice: 25, type: 'manufactured' },
  { code: 'HLV', name: 'Half Liver', department: 'kitchen', category: 'liver', sellingPrice: 20, type: 'manufactured' },
  { code: 'FLV', name: 'Full Liver', department: 'kitchen', category: 'liver', sellingPrice: 35, type: 'manufactured' },
  { code: 'HST', name: 'Half Steak', department: 'kitchen', category: 'steak', sellingPrice: 30, type: 'manufactured' },
  { code: 'STO', name: 'Steak Only', department: 'kitchen', category: 'steak', sellingPrice: 40, type: 'manufactured' },
  { code: 'KHW', name: 'Khwanda', department: 'kitchen', category: 'khwanda', sellingPrice: 25, type: 'manufactured' },
  { code: 'LIV', name: 'Liver', department: 'kitchen', category: 'liver', sellingPrice: 30, type: 'manufactured' },
  { code: 'MAK', name: 'Makhaha', department: 'kitchen', category: 'makhaha', sellingPrice: 20, type: 'manufactured' },
  { code: 'MAL', name: 'Mala', department: 'kitchen', category: 'mala', sellingPrice: 35, type: 'manufactured' },
  { code: 'MAS', name: 'Mashonzha', department: 'kitchen', category: 'mashonzha', sellingPrice: 40, type: 'manufactured' },
  { code: 'MKP', name: 'Mala Kota Plate', department: 'kitchen', category: 'mala', sellingPrice: 45, type: 'manufactured' },
  { code: 'MSP', name: 'Mashonzha Plate', department: 'kitchen', category: 'mashonzha', sellingPrice: 50, type: 'manufactured' },
  { code: 'MUK', name: 'Mukokoroshi', department: 'kitchen', category: 'mukokoroshi', sellingPrice: 30, type: 'manufactured' },
  { code: 'MUR', name: 'Muroho wa Thanga', department: 'kitchen', category: 'muroho', sellingPrice: 15, type: 'manufactured' },
  { code: 'MUT', name: 'Mutuku', department: 'kitchen', category: 'mutuku', sellingPrice: 10, type: 'manufactured' },
  { code: 'PAP', name: 'Pap Only', department: 'kitchen', category: 'pap', sellingPrice: 10, type: 'manufactured' },
  { code: 'RUS', name: 'Russian', department: 'kitchen', category: 'russian', sellingPrice: 10, type: 'manufactured' },
  { code: 'SAL', name: 'Salad', department: 'kitchen', category: 'salad', sellingPrice: 10, type: 'manufactured' },
  { code: 'STV', name: 'Stew Vors', department: 'kitchen', category: 'vors', sellingPrice: 30, type: 'manufactured' },
  { code: 'FSTV', name: 'Full Stew Vors', department: 'kitchen', category: 'vors', sellingPrice: 40, type: 'manufactured' },
  { code: 'HSTV', name: 'Half Stew Vors', department: 'kitchen', category: 'vors', sellingPrice: 20, type: 'manufactured' },
  { code: 'VOR', name: 'Vors', department: 'kitchen', category: 'vors', sellingPrice: 25, type: 'manufactured' },
  { code: 'CBG', name: 'Cabbage', department: 'kitchen', category: 'cabbage', sellingPrice: 10, type: 'manufactured' },
  { code: 'khp', name: 'Khwanda Plate', department: 'kitchen', category: 'khwanda', sellingPrice: 35, type: 'manufactured' },
  { code: 'lvp', name: 'Liver Plate', department: 'kitchen', category: 'liver', sellingPrice: 40, type: 'manufactured' },
  { code: 'mlp', name: 'Mala Plate', department: 'kitchen', category: 'mala', sellingPrice: 45, type: 'manufactured' },
  { code: 'GRV', name: 'Gravy', department: 'kitchen', category: 'gravy', sellingPrice: 5, type: 'manufactured' },

  // ── Bakery ──
  { code: 'BHBB', name: 'Buttered Half Brown Bread', department: 'bakery', category: 'bread', sellingPrice: 8, type: 'manufactured' },
  { code: 'BHBJ', name: 'Buttered Half Bread w/ Jam', department: 'bakery', category: 'bread', sellingPrice: 10, type: 'manufactured' },
  { code: 'BHW', name: 'Buttered Half White', department: 'bakery', category: 'bread', sellingPrice: 8, type: 'manufactured' },
  { code: 'BKT', name: 'Biscuit', department: 'bakery', category: 'biscuit', sellingPrice: 5, type: 'manufactured' },
  { code: 'BLB', name: 'Brown Loaf Bread', department: 'bakery', category: 'bread', sellingPrice: 13, type: 'manufactured' },
  { code: 'bhs', name: 'Buttered Half Sandwich', department: 'bakery', category: 'bread', sellingPrice: 7, type: 'manufactured' },
  { code: 'Gin', name: 'Ginger', department: 'bakery', category: 'ginger', sellingPrice: 3, type: 'manufactured' },
  { code: 'HB', name: 'Half Bread', department: 'bakery', category: 'bread', sellingPrice: 7, type: 'manufactured' },
  { code: 'HDB', name: 'Hotdog Bun', department: 'bakery', category: 'buns', sellingPrice: 10, type: 'manufactured' },
  { code: 'HDV', name: 'Hotdog Vienna', department: 'bakery', category: 'hotdog', sellingPrice: 12, type: 'manufactured' },
  { code: 'HW', name: 'Half White', department: 'bakery', category: 'bread', sellingPrice: 7, type: 'manufactured' },
  { code: 'KO', name: 'Kota', department: 'bakery', category: 'kota', sellingPrice: 25, type: 'manufactured' },
  { code: 'LBB', name: 'Loaf Brown Bread', department: 'bakery', category: 'bread', sellingPrice: 13, type: 'manufactured' },
  { code: 'LWB', name: 'Loaf White Bread', department: 'bakery', category: 'bread', sellingPrice: 14, type: 'manufactured' },
  { code: 'MUF', name: 'Muffin', department: 'bakery', category: 'muffin', sellingPrice: 12, type: 'manufactured' },
  { code: 'POR', name: 'Porridge', department: 'bakery', category: 'porridge', sellingPrice: 8, type: 'manufactured' },
  { code: 'ROL', name: 'Rolls', department: 'bakery', category: 'rolls', sellingPrice: 15, type: 'manufactured' },
  { code: 'S1', name: 'Scone', department: 'bakery', category: 'scones', sellingPrice: 3, type: 'manufactured' },
  { code: 'SB4', name: 'School Buns x4', department: 'bakery', category: 'schoolbuns', sellingPrice: 8, type: 'manufactured' },
  { code: 'SB6', name: 'School Buns x6', department: 'bakery', category: 'schoolbuns', sellingPrice: 12, type: 'manufactured' },
  { code: 'SB8', name: 'School Buns x8', department: 'bakery', category: 'schoolbuns', sellingPrice: 15, type: 'manufactured' },
  { code: 'SB10', name: 'School Buns x10', department: 'bakery', category: 'schoolbuns', sellingPrice: 18, type: 'manufactured' },
  { code: 'SB12', name: 'School Buns x12', department: 'bakery', category: 'schoolbuns', sellingPrice: 23, type: 'manufactured' },
  { code: 'SB30', name: 'School Buns x30', department: 'bakery', category: 'schoolbuns', sellingPrice: 50, type: 'manufactured' },
  { code: 'SCH', name: 'Scone Half', department: 'bakery', category: 'scones', sellingPrice: 2, type: 'manufactured' },
  { code: 'SCB3', name: 'SCB x3', department: 'bakery', category: 'scones', sellingPrice: 6, type: 'manufactured' },
  { code: 'f2', name: 'Fatcook', department: 'bakery', category: 'fatcook', sellingPrice: 3, type: 'manufactured' },
  { code: 'yr', name: 'Yellow Rolls', department: 'bakery', category: 'rolls', sellingPrice: 12, type: 'manufactured' },
  { code: 'hsw', name: 'Half Sandwich White', department: 'bakery', category: 'bread', sellingPrice: 7, type: 'manufactured' },

  // ── Sandwiches ──
  { code: 'BEG', name: 'Boiled Egg Sandwich', department: 'sandwich', category: 'sandwich', sellingPrice: 8, type: 'manufactured' },
  { code: 'S11', name: 'Sandwich S11', department: 'sandwich', category: 'sandwich', sellingPrice: 11, type: 'manufactured' },
  { code: 'S13', name: 'Sandwich S13', department: 'sandwich', category: 'sandwich', sellingPrice: 13, type: 'manufactured' },
  { code: 'S15', name: 'Sandwich S15', department: 'sandwich', category: 'sandwich', sellingPrice: 15, type: 'manufactured' },
  { code: 'S17', name: 'Sandwich S17', department: 'sandwich', category: 'sandwich', sellingPrice: 17, type: 'manufactured' },
  { code: 'S18', name: 'Sandwich S18', department: 'sandwich', category: 'sandwich', sellingPrice: 18, type: 'manufactured' },
  { code: 'S19', name: 'Sandwich S19', department: 'sandwich', category: 'sandwich', sellingPrice: 19, type: 'manufactured' },
  { code: 'S21', name: 'Sandwich S21', department: 'sandwich', category: 'sandwich', sellingPrice: 21, type: 'manufactured' },
  { code: 'S23', name: 'Chicken Mayo Sandwich', department: 'sandwich', category: 'sandwich', sellingPrice: 23, type: 'manufactured' },

  // ── Soft Drinks ──
  { code: 'COKE300', name: 'Coke 300ml', department: 'drinks', category: 'cola', sellingPrice: 12, type: 'resale' },
  { code: 'COKE500', name: 'Coke 500ml', department: 'drinks', category: 'cola', sellingPrice: 18, type: 'resale' },
  { code: 'FANTA300', name: 'Fanta 300ml', department: 'drinks', category: 'fanta', sellingPrice: 12, type: 'resale' },
  { code: 'SPRITE300', name: 'Sprite 300ml', department: 'drinks', category: 'sprite', sellingPrice: 12, type: 'resale' },
  { code: 'STONEY300', name: 'Stoney 300ml', department: 'drinks', category: 'stoney', sellingPrice: 12, type: 'resale' },
  { code: 'OASIS', name: 'Oasis', department: 'drinks', category: 'oasis', sellingPrice: 8, type: 'resale' },
  { code: 'DRAGON', name: 'Dragon Energy', department: 'drinks', category: 'energy', sellingPrice: 15, type: 'resale' },
  { code: 'PLAY', name: 'Play', department: 'drinks', category: 'play', sellingPrice: 8, type: 'resale' },
  { code: 'PRED', name: 'Predator', department: 'drinks', category: 'energy', sellingPrice: 15, type: 'resale' },
  { code: 'BURN', name: 'Burn', department: 'drinks', category: 'energy', sellingPrice: 18, type: 'resale' },

  // ── Tea & Hot Drinks ──
  { code: 'JOKO', name: 'Joko Tea', department: 'tea', category: 'tea', sellingPrice: 5, type: 'manufactured' },
  { code: 'RB', name: 'Rooibos', department: 'tea', category: 'tea', sellingPrice: 5, type: 'manufactured' },
  { code: 'FIVE', name: 'Five Roses', department: 'tea', category: 'tea', sellingPrice: 5, type: 'manufactured' },
  { code: 'Rico', name: 'Ricoffy', department: 'tea', category: 'coffee', sellingPrice: 8, type: 'manufactured' },
  { code: 'WAT', name: 'Hot Water', department: 'tea', category: 'water', sellingPrice: 3, type: 'manufactured' },
  { code: 'GTA', name: 'Green Tea', department: 'tea', category: 'tea', sellingPrice: 5, type: 'manufactured' },
  { code: 'CUP', name: 'Cup (empty)', department: 'tea', category: 'cup', sellingPrice: 2, type: 'resale' },

  // ── Sweets & Chips ──
  { code: 'CHAP', name: 'Chappies', department: 'sweets', category: 'sweets', sellingPrice: 1, type: 'resale' },
  { code: 'HAL', name: 'Halls', department: 'sweets', category: 'sweets', sellingPrice: 2, type: 'resale' },
  { code: 'SMOOTH', name: 'Smoothie', department: 'sweets', category: 'sweets', sellingPrice: 5, type: 'resale' },
  { code: 'POPS', name: 'Pop Shots', department: 'sweets', category: 'chips', sellingPrice: 5, type: 'resale' },
  { code: 'BSWT', name: 'Baby Sweets', department: 'sweets', category: 'sweets', sellingPrice: 1, type: 'resale' },
  { code: 'SNKIT', name: 'Snack-It', department: 'sweets', category: 'chips', sellingPrice: 5, type: 'resale' },

  // ── Miscellaneous ──
  { code: 'MATCHES', name: 'Matches', department: 'misc', category: 'misc', sellingPrice: 2, type: 'resale' },
  { code: 'PLA', name: 'Plastics', department: 'misc', category: 'packaging', sellingPrice: 3, type: 'resale' },
  { code: 'PIN', name: 'Pin', department: 'misc', category: 'misc', sellingPrice: 1, type: 'resale' },
  { code: 'POP', name: 'Popcorn', department: 'misc', category: 'snacks', sellingPrice: 5, type: 'resale' },
  { code: 'CHG', name: 'Charging', department: 'misc', category: 'services', sellingPrice: 5, type: 'resale' },
  { code: 'GRANDPA', name: 'Grandpa', department: 'misc', category: 'medicine', sellingPrice: 5, type: 'resale' },
]

// ────────────────────────────────────────────
// Inventory / Ingredients
// ────────────────────────────────────────────

export const INVENTORY_SEED = [
  // Meat & Protein
  { name: 'Maize Meal', unit: 'kg', currentStock: 50, reorderLevel: 10, location: 'kitchen', category: 'dry-goods' },
  { name: 'Intestines (Mutuku)', unit: 'kg', currentStock: 0, reorderLevel: 5, location: 'kitchen', category: 'meat' },
  { name: 'Chicken Feet (Mala)', unit: 'kg', currentStock: 0, reorderLevel: 5, location: 'kitchen', category: 'meat' },
  { name: 'Beef Steak', unit: 'kg', currentStock: 0, reorderLevel: 3, location: 'kitchen', category: 'meat' },
  { name: 'Chicken Gizzards (Khwanda)', unit: 'kg', currentStock: 0, reorderLevel: 3, location: 'kitchen', category: 'meat' },
  { name: 'Liver', unit: 'kg', currentStock: 0, reorderLevel: 3, location: 'kitchen', category: 'meat' },
  { name: 'Mukokoroshi', unit: 'kg', currentStock: 0, reorderLevel: 3, location: 'kitchen', category: 'meat' },
  { name: 'Mashonzha', unit: 'kg', currentStock: 0, reorderLevel: 2, location: 'kitchen', category: 'meat' },
  { name: 'Makhaha', unit: 'kg', currentStock: 0, reorderLevel: 2, location: 'kitchen', category: 'meat' },
  { name: 'Vors / Russian', unit: 'kg', currentStock: 0, reorderLevel: 3, location: 'kitchen', category: 'meat' },
  { name: 'Vienna Sausages', unit: 'kg', currentStock: 0, reorderLevel: 2, location: 'kitchen', category: 'meat' },

  // Bakery Ingredients
  { name: 'Flour (Bread)', unit: 'kg', currentStock: 25, reorderLevel: 10, location: 'bakery', category: 'bakery' },
  { name: 'Flour (Cake)', unit: 'kg', currentStock: 10, reorderLevel: 5, location: 'bakery', category: 'bakery' },
  { name: 'Yeast', unit: 'g', currentStock: 500, reorderLevel: 200, location: 'bakery', category: 'bakery' },
  { name: 'Sugar', unit: 'kg', currentStock: 10, reorderLevel: 5, location: 'bakery', category: 'bakery' },
  { name: 'Salt', unit: 'kg', currentStock: 5, reorderLevel: 2, location: 'bakery', category: 'bakery' },
  { name: 'Butter / Margarine (Rama)', unit: 'kg', currentStock: 5, reorderLevel: 2, location: 'bakery', category: 'bakery' },
  { name: 'Cooking Oil', unit: 'L', currentStock: 10, reorderLevel: 5, location: 'kitchen', category: 'dry-goods' },
  { name: 'Eggs', unit: 'pieces', currentStock: 30, reorderLevel: 12, location: 'kitchen', category: 'fresh' },
  { name: 'Milk', unit: 'L', currentStock: 5, reorderLevel: 3, location: 'bakery', category: 'fresh' },
  { name: 'Baking Powder', unit: 'g', currentStock: 500, reorderLevel: 200, location: 'bakery', category: 'bakery' },
  { name: 'Muffin Mix', unit: 'kg', currentStock: 2, reorderLevel: 1, location: 'bakery', category: 'bakery' },

  // Sandwich Ingredients
  { name: 'Bread (White Loaf)', unit: 'pieces', currentStock: 10, reorderLevel: 5, location: 'bakery', category: 'bread' },
  { name: 'Bread (Brown Loaf)', unit: 'pieces', currentStock: 10, reorderLevel: 5, location: 'bakery', category: 'bread' },
  { name: 'Polony', unit: 'kg', currentStock: 2, reorderLevel: 1, location: 'kitchen', category: 'cold-meats' },
  { name: 'Cheese', unit: 'kg', currentStock: 1, reorderLevel: 0.5, location: 'kitchen', category: 'dairy' },
  { name: 'Lettuce', unit: 'pieces', currentStock: 5, reorderLevel: 2, location: 'kitchen', category: 'fresh' },
  { name: 'Tomato', unit: 'kg', currentStock: 2, reorderLevel: 1, location: 'kitchen', category: 'fresh' },
  { name: 'Mayonnaise', unit: 'kg', currentStock: 2, reorderLevel: 1, location: 'kitchen', category: 'condiments' },
  { name: 'Hotdog Buns', unit: 'pieces', currentStock: 20, reorderLevel: 10, location: 'bakery', category: 'bread' },

  // Spices & Condiments
  { name: 'Spices (Assorted)', unit: 'kg', currentStock: 1, reorderLevel: 0.5, location: 'kitchen', category: 'spices' },
  { name: 'Cabbage', unit: 'kg', currentStock: 3, reorderLevel: 1, location: 'kitchen', category: 'fresh' },
  { name: 'Pumpkin Leaves (Muroho)', unit: 'kg', currentStock: 2, reorderLevel: 1, location: 'kitchen', category: 'fresh' },
  { name: 'Archaar', unit: 'kg', currentStock: 1, reorderLevel: 0.5, location: 'kitchen', category: 'condiments' },
  { name: 'Chilli', unit: 'kg', currentStock: 0.5, reorderLevel: 0.2, location: 'kitchen', category: 'spices' },
  { name: 'Chicken (Cooked)', unit: 'kg', currentStock: 0, reorderLevel: 2, location: 'kitchen', category: 'meat' },
]

// ────────────────────────────────────────────
// Suppliers
// ────────────────────────────────────────────

export const SUPPLIERS_SEED = [
  { name: 'Meat Supplier', category: 'Meat', contactPerson: '', phone: '', email: '', address: '', paymentTerms: 'Cash on delivery' },
  { name: 'Bakery Supplier / Mill', category: 'Bakery', contactPerson: '', phone: '', email: '', address: '', paymentTerms: 'Weekly' },
  { name: 'Grocery / Dry Goods', category: 'Groceries', contactPerson: '', phone: '', email: '', address: '', paymentTerms: 'Cash' },
  { name: 'Beverages Distributor', category: 'Beverages', contactPerson: '', phone: '', email: '', address: '', paymentTerms: 'COD' },
  { name: 'Packaging Supplier', category: 'Packaging', contactPerson: '', phone: '', email: '', address: '', paymentTerms: 'Monthly' },
]

// ────────────────────────────────────────────
// Sample Recipes
// ────────────────────────────────────────────

// These will be linked to inventory items by name after seeding
export const RECIPES_SEED = [
  {
    productCode: 'PAP',
    name: 'Pap Only',
    batchYield: 1,
    ingredients: [
      { ingredientName: 'Maize Meal', qty: 200, unit: 'g' },
      { ingredientName: 'Salt', qty: 2, unit: 'g' },
    ]
  },
  {
    productCode: 'MUT',
    name: 'Mutuku',
    batchYield: 1,
    ingredients: [
      { ingredientName: 'Intestines (Mutuku)', qty: 150, unit: 'g' },
      { ingredientName: 'Spices (Assorted)', qty: 5, unit: 'g' },
      { ingredientName: 'Cooking Oil', qty: 20, unit: 'ml' },
    ]
  },
  {
    productCode: 'S1',
    name: 'Scone',
    batchYield: 20,
    ingredients: [
      { ingredientName: 'Flour (Cake)', qty: 1000, unit: 'g' },
      { ingredientName: 'Sugar', qty: 100, unit: 'g' },
      { ingredientName: 'Butter / Margarine (Rama)', qty: 100, unit: 'g' },
      { ingredientName: 'Milk', qty: 300, unit: 'ml' },
      { ingredientName: 'Baking Powder', qty: 30, unit: 'g' },
      { ingredientName: 'Eggs', qty: 2, unit: 'pieces' },
    ]
  },
  {
    productCode: 'SB30',
    name: 'School Buns x30',
    batchYield: 30,
    ingredients: [
      { ingredientName: 'Flour (Bread)', qty: 2, unit: 'kg' },
      { ingredientName: 'Yeast', qty: 20, unit: 'g' },
      { ingredientName: 'Sugar', qty: 200, unit: 'g' },
      { ingredientName: 'Salt', qty: 10, unit: 'g' },
      { ingredientName: 'Cooking Oil', qty: 100, unit: 'ml' },
    ]
  },
  {
    productCode: 'f2',
    name: 'Fatcook',
    batchYield: 15,
    ingredients: [
      { ingredientName: 'Flour (Bread)', qty: 1, unit: 'kg' },
      { ingredientName: 'Sugar', qty: 50, unit: 'g' },
      { ingredientName: 'Cooking Oil', qty: 500, unit: 'ml' },
      { ingredientName: 'Yeast', qty: 10, unit: 'g' },
    ]
  },
  {
    productCode: 'LBB',
    name: 'Loaf Brown Bread',
    batchYield: 1,
    ingredients: [
      { ingredientName: 'Flour (Bread)', qty: 500, unit: 'g' },
      { ingredientName: 'Yeast', qty: 7, unit: 'g' },
      { ingredientName: 'Salt', qty: 5, unit: 'g' },
      { ingredientName: 'Sugar', qty: 10, unit: 'g' },
    ]
  },
  {
    productCode: 'BEG',
    name: 'Boiled Egg Sandwich',
    batchYield: 1,
    ingredients: [
      { ingredientName: 'Eggs', qty: 1, unit: 'pieces' },
      { ingredientName: 'Bread (White Loaf)', qty: 0.1, unit: 'pieces' },
    ]
  },
  {
    productCode: 'S23',
    name: 'Chicken Mayo Sandwich',
    batchYield: 1,
    ingredients: [
      { ingredientName: 'Bread (White Loaf)', qty: 0.1, unit: 'pieces' },
      { ingredientName: 'Chicken (Cooked)', qty: 80, unit: 'g' },
      { ingredientName: 'Mayonnaise', qty: 20, unit: 'g' },
      { ingredientName: 'Lettuce', qty: 0.1, unit: 'pieces' },
    ]
  },
]

// ────────────────────────────────────────────
// Default Overheads
// ────────────────────────────────────────────

export const OVERHEADS_SEED = [
  { name: 'Rent', category: 'fixed', amount: 0, frequency: 'monthly' },
  { name: 'Electricity', category: 'fixed', amount: 0, frequency: 'monthly' },
  { name: 'Staff Wages', category: 'fixed', amount: 0, frequency: 'monthly' },
  { name: 'Gas / Fuel', category: 'variable', amount: 0, frequency: 'monthly' },
  { name: 'Packaging', category: 'variable', amount: 0, frequency: 'monthly' },
  { name: 'Equipment Maintenance', category: 'variable', amount: 0, frequency: 'monthly' },
  { name: 'Internet', category: 'fixed', amount: 0, frequency: 'monthly' },
  { name: 'Cleaning', category: 'variable', amount: 0, frequency: 'monthly' },
]

// ────────────────────────────────────────────
// Flag thresholds
// ────────────────────────────────────────────

export const FLAG_THRESHOLDS = {
  lowMarginPct: 20,
  highCogsVariancePct: 15,
  stocktakeTolerancePct: 5,
  salesDropPct: 20,
}

export function getFlagThresholds() {
  try {
    const raw = localStorage.getItem('desres_pos_flagThresholds')
    return raw ? JSON.parse(raw) : FLAG_THRESHOLDS
  } catch { return FLAG_THRESHOLDS }
}

export function saveFlagThresholds(thresholds) {
  localStorage.setItem('desres_pos_flagThresholds', JSON.stringify(thresholds))
}

// ────────────────────────────────────────────
// Seed function — run once
// ────────────────────────────────────────────

export function seedPosData() {
  const seeded = localStorage.getItem('desres_pos_seeded')
  if (seeded) return false

  // Seed products with departmentId based on POS sub-category
  for (const p of POS_PRODUCTS_SEED) {
    const departmentId = posDeptToBizDept(p.department)
    savePosProduct({ ...p, departmentId, isActive: true })
  }

  // Seed inventory with departmentId based on location
  const invMap = {}
  for (const item of INVENTORY_SEED) {
    const departmentId = item.location === 'bakery' ? 'bakery' : 'kitchen'
    const saved = saveInventoryItem({ ...item, departmentId, reorderQty: item.reorderLevel * 2, avgCost: 0, lastPurchasePrice: 0 })
    invMap[item.name] = saved.id
  }

  // Seed suppliers (shared across Desres Kitchen branch)
  for (const s of SUPPLIERS_SEED) {
    saveSupplier({ ...s, departmentId: 'kitchen' })
  }

  // Seed recipes (link ingredient names to inventory IDs)
  const allProducts = JSON.parse(localStorage.getItem('desres_pos_products') || '[]')
  for (const r of RECIPES_SEED) {
    const product = allProducts.find(p => p.code === r.productCode)
    if (product) {
      const ingredients = r.ingredients.map(ing => ({
        ...ing,
        inventoryItemId: invMap[ing.ingredientName] || null,
      }))
      saveRecipe({
        productId: product.id,
        productCode: r.productCode,
        name: r.name,
        batchYield: r.batchYield,
        departmentId: product.departmentId,
        ingredients,
      })
    }
  }

  // Seed overheads per department
  const month = new Date().toISOString().slice(0, 7)
  for (const dept of ['kitchen', 'bakery']) {
    for (const o of OVERHEADS_SEED) {
      saveOverhead({ ...o, departmentId: dept, month })
    }
  }

  // Save flag thresholds
  saveFlagThresholds(FLAG_THRESHOLDS)

  localStorage.setItem('desres_pos_seeded', 'true')
  return true
}
